/**
 * Standalone helpers for the Response plugin's Ctrl+Click URI exploration feature.
 *
 * These functions are intentionally free of any DOM or CodeMirror dependencies so
 * that they can be unit-tested in isolation.
 */

// Matches angle-bracket IRIs as they appear in Turtle / N-Triples / TriG, e.g. `<http://example.org/foo>`
const ANGLE_IRI_RE = /<([^<>\s"{}|\\^`]+)>/g;
// Matches bare or quoted URIs as they appear in JSON / XML / CSV responses, e.g. `"http://example.org/foo"`
const BARE_URI_RE = /(?:https?|urn|ftp|mailto):[^\s<>"'`{}|\\^[\]]+/g;
// Matches Turtle/SPARQL prefix declarations such as `@prefix ex: <...> .` and `PREFIX ex: <...>`.
const PREFIX_DECL_RE = /^\s*(?:@prefix|PREFIX)\s+(\w*):\s*<[^>]+>\s*\.?\s*$/i;

/**
 * Remove trailing punctuation that is commonly adjacent to a URI in a serialized
 * response (e.g. the `.` terminating a Turtle statement) but is not part of the URI.
 */
function sanitizeUri(uri: string): string {
  return uri.replace(/[.,;)\]]+$/, "").trim();
}

/**
 * Find the URI located at the given character offset within a serialized SPARQL response.
 *
 * Angle-bracket IRIs (Turtle/N-Triples style) take precedence over bare URIs so that a
 * click inside `<http://…>` returns the full IRI rather than the embedded `http://…` run.
 *
 * @param text   The full response text as shown in the response view.
 * @param offset The character offset (0-based) of the click within `text`.
 * @returns The URI at that offset, or `undefined` when the offset is not on a URI.
 */
export function extractUriAtOffset(text: string, offset: number): string | undefined {
  if (!text || offset < 0 || offset >= text.length) return undefined;

  let match: RegExpExecArray | null;

  ANGLE_IRI_RE.lastIndex = 0;
  while ((match = ANGLE_IRI_RE.exec(text)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    if (offset >= start && offset <= end) {
      const uri = sanitizeUri(match[1]);
      return uri.length > 0 ? uri : undefined;
    }
  }

  BARE_URI_RE.lastIndex = 0;
  while ((match = BARE_URI_RE.exec(text)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    if (offset >= start && offset <= end) {
      const uri = sanitizeUri(match[0]);
      return uri.length > 0 ? uri : undefined;
    }
  }

  return undefined;
}

/**
 * Build a CONSTRUCT query that retrieves all triples where the given URI is the subject.
 *
 * @param uri The URI to look up as a subject.
 * @returns A SPARQL `CONSTRUCT` query string.
 */
export function buildSubjectOfQuery(uri: string): string {
  return `CONSTRUCT { <${uri}> ?p ?o } WHERE { <${uri}> ?p ?o }`;
}

/**
 * Build a CONSTRUCT query that retrieves all triples where the given URI is the object.
 *
 * @param uri The URI to look up as an object.
 * @returns A SPARQL `CONSTRUCT` query string.
 */
export function buildObjectOfQuery(uri: string): string {
  return `CONSTRUCT { ?s ?p <${uri}> } WHERE { ?s ?p <${uri}> } LIMIT 1000`;
}

/**
 * Remove duplicate prefix declarations from a text block.
 *
 * Prefixes are considered duplicates by label (case-insensitive), and can be
 * deduplicated against already-rendered content in the response viewer.
 */
export function stripDuplicatePrefixDeclarations(text: string, existingText = ""): string {
  if (!text) return text;

  const seenLabels = new Set<string>();
  const addSeenLabels = (source: string) => {
    for (const line of source.split("\n")) {
      const match = line.match(PREFIX_DECL_RE);
      if (!match) continue;
      seenLabels.add(match[1].toLowerCase());
    }
  };

  addSeenLabels(existingText);

  const deduplicatedLines: string[] = [];
  for (const line of text.split("\n")) {
    const match = line.match(PREFIX_DECL_RE);
    if (!match) {
      deduplicatedLines.push(line);
      continue;
    }

    const label = match[1].toLowerCase();
    if (seenLabels.has(label)) continue;

    seenLabels.add(label);
    deduplicatedLines.push(line);
  }

  return deduplicatedLines.join("\n");
}
